/**
 * Zone Geometry Utilities
 *
 * Utility functions for processing and normalizing zone geometries using Turf.js
 */

// @ts-ignore - Turf.js types
import * as turf from '@turf/turf';
import type { Feature, Polygon, MultiPolygon } from 'geojson';

export interface GeoJSONGeometry {
  type: string;
  coordinates: number[] | number[][] | number[][][] | number[][][][];
}

export interface NormalizedGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
}

/**
 * Convert geometry to Turf Feature based on type
 */
function convertToFeature(geometry: GeoJSONGeometry): Feature<Polygon | MultiPolygon> {
  if (geometry.type === 'LineString') {
    const line = turf.lineString(geometry.coordinates as number[][]);
    const buffered = turf.buffer(line, 250, { units: 'meters' });
    if (!buffered) {
      throw new Error('Failed to buffer LineString to Polygon');
    }
    return buffered;
  } else if (geometry.type === 'Polygon') {
    return turf.polygon(geometry.coordinates as number[][][]);
  } else if (geometry.type === 'MultiPolygon') {
    return turf.multiPolygon(geometry.coordinates as number[][][][]);
  } else if (geometry.type === 'Point') {
    const point = turf.point(geometry.coordinates as number[]);
    const buffered = turf.buffer(point, 1000, { units: 'meters' });
    if (!buffered) {
      throw new Error('Failed to buffer Point to Polygon');
    }
    return buffered;
  } else {
    throw new Error(`Unsupported geometry type: ${geometry.type}`);
  }
}

/**
 * Process and clean the normalized geometry
 */
function processGeometry(
  normalized: Feature<Polygon | MultiPolygon>
): Feature<Polygon | MultiPolygon> {
  // Clean duplicate coordinates
  let processed = turf.cleanCoords(normalized);

  // Simplify complex polygons to reduce database load
  const area = turf.area(processed);
  if (area > 0.01) {
    processed = turf.simplify(processed, { tolerance: 0.0001, highQuality: true });
  }

  // Fix self-intersecting polygons (unkink)
  if (processed.geometry.type === 'Polygon') {
    const unkinked = turf.unkinkPolygon(processed as Feature<Polygon>);
    if (unkinked.features.length > 0) {
      processed = unkinked.features[0] as Feature<Polygon | MultiPolygon>;
    }
  }

  // Validate polygon orientation (should be counter-clockwise for exterior rings)
  if (processed.geometry.type === 'Polygon') {
    const coords = processed.geometry.coordinates;
    const exterior = coords[0];
    if (exterior && exterior.length > 0) {
      if (turf.booleanClockwise(turf.lineString(exterior))) {
        coords[0] = [...exterior].reverse();
      }
    }
  }

  return processed;
}

/**
 * Normalize a geometry from Leaflet Draw to a clean Polygon/MultiPolygon
 * Handles freehand LineString conversion, coordinate cleaning, and simplification
 * Also handles Circle and Rectangle shapes from Leaflet.draw
 */
export function normalizeGeometry(geometry: GeoJSONGeometry): NormalizedGeometry {
  const normalized = convertToFeature(geometry);
  const processed = processGeometry(normalized);
  return processed.geometry as NormalizedGeometry;
}

/**
 * Calculate the area of a geometry in square kilometers
 */
export function calculateArea(geometry: NormalizedGeometry): number {
  const feature =
    geometry.type === 'Polygon'
      ? turf.polygon(geometry.coordinates as number[][][])
      : turf.multiPolygon(geometry.coordinates as number[][][][]);

  // Area is returned in square meters, convert to square kilometers
  return turf.area(feature) / 1000000;
}

/**
 * Check if a geometry is too large (warn user if > 1000 sq km)
 */
export function isGeometryTooLarge(geometry: NormalizedGeometry): boolean {
  const area = calculateArea(geometry);
  return area > 1000; // 1000 square kilometers
}

/**
 * Get bounding box of a geometry [minLng, minLat, maxLng, maxLat]
 */
export function getBoundingBox(geometry: NormalizedGeometry): [number, number, number, number] {
  const feature: Feature<Polygon | MultiPolygon> =
    geometry.type === 'Polygon'
      ? turf.polygon(geometry.coordinates as number[][][])
      : turf.multiPolygon(geometry.coordinates as number[][][][]);

  const bbox = turf.bbox(feature);
  return [bbox[0], bbox[1], bbox[2], bbox[3]];
}

/**
 * Keep only the points that fall inside at least one of the given zone geometries.
 *
 * Each zone's bounding box is computed once and checked first, so the exact
 * point-in-polygon test only runs against the few zones whose box actually contains
 * the point. Zones without a Polygon/MultiPolygon geometry are ignored, which means an
 * empty or geometry-less zone list keeps nothing.
 */
export function filterPointsInZones<
  T extends { coordinates: { latitude: number; longitude: number } }
>(points: readonly T[], geometries: readonly (NormalizedGeometry | undefined)[]): T[] {
  const zones = geometries
    .filter(
      (g): g is NormalizedGeometry =>
        !!g && (g.type === 'Polygon' || g.type === 'MultiPolygon') && !!g.coordinates
    )
    .map(geometry => {
      const feature: Feature<Polygon | MultiPolygon> =
        geometry.type === 'Polygon'
          ? turf.polygon(geometry.coordinates as number[][][])
          : turf.multiPolygon(geometry.coordinates as number[][][][]);
      const [minLng, minLat, maxLng, maxLat] = turf.bbox(feature);
      return { feature, minLng, minLat, maxLng, maxLat };
    });
  if (zones.length === 0) return [];

  return points.filter(({ coordinates: { latitude, longitude } }) =>
    zones.some(
      zone =>
        longitude >= zone.minLng &&
        longitude <= zone.maxLng &&
        latitude >= zone.minLat &&
        latitude <= zone.maxLat &&
        turf.booleanPointInPolygon(turf.point([longitude, latitude]), zone.feature)
    )
  );
}
